import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ParticipantEditModal = ({ isOpen, onClose, participant, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    payment: 'pending',
    isMember: false
  });

  const [isLoading, setIsLoading] = useState(false);

  const paymentOptions = useMemo(() => {
    return [
      { value: 'paid', label: '완료' },
      { value: 'pending', label: '대기' },
      { value: 'overdue', label: '미납' }
    ];
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      name: participant?.name || '',
      phone: participant?.phone || '',
      payment: participant?.payment || 'pending',
      isMember: Boolean(participant?.isMember)
    });
  }, [isOpen, participant]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!participant?.id) return;

    const name = String(formData?.name || '').trim();
    const phone = String(formData?.phone || '').trim();

    if (!name) {
      alert('이름을 입력해주세요.');
      return;
    }
    if (!phone) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      await onSave({
        ...participant,
        name,
        phone,
        payment: formData?.payment,
        isMember: Boolean(formData?.isMember)
      });
      onClose();
    } catch (err) {
      alert(err?.message || '저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-300 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background z-250" aria-hidden="true" />
      <div className="relative z-300 w-full max-w-xl bg-card rounded-xl shadow-2xl border border-border my-8">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">참가자 수정</h2>
              <p className="text-sm caption text-muted-foreground">참가자 정보를 수정합니다</p>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic active-press disabled:opacity-50"
              aria-label="닫기"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="이름"
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                disabled={isLoading}
              />
              <Input
                label="전화번호"
                type="tel"
                value={formData?.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e?.target?.value }))}
                disabled={isLoading}
              />
            </div>

            <Select
              label="결제 여부"
              options={paymentOptions}
              value={formData?.payment}
              onChange={(value) => setFormData(prev => ({ ...prev, payment: value }))}
              disabled={isLoading}
            />

            <div className="flex items-center gap-2">
              <Checkbox
                checked={Boolean(formData?.isMember)}
                onChange={(e) => setFormData(prev => ({ ...prev, isMember: Boolean(e?.target?.checked) }))}
                disabled={isLoading}
                label="정회원(회원으로 등록)"
              />
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
                fullWidth
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={isLoading}
                iconName="Save"
                iconPosition="right"
                fullWidth
              >
                저장
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ParticipantEditModal;
